"use client";

// Rafale (façon Match Madness de Duolingo) : relier un maximum de paires en
// 45 secondes, sur les match_pairs des leçons d'une unité TERMINÉE. Pur moment
// de révision-plaisir : aucune récompense serveur (rien à créditer, rien à
// tricher), juste un score de session. Sobre côté animations (fondu + petites
// translations), conformément au design system.
import { useEffect, useMemo, useRef, useState } from "react";
import { useTranslations, useLocale } from "next-intl";
import { motion, AnimatePresence } from "motion/react";
import SolarIcon from "@/components/ui/SolarIcon/SolarIcon";

const GAME_SECONDS = 45;
const ROUND_SIZE = 5;

const CARD_BASE =
    "w-full rounded-2xl border-2 border-b-4 px-3 py-3 text-left text-sm font-medium transition-colors duration-150 disabled:cursor-default";
const CARD_NEUTRAL =
    "border-gray-200 border-b-gray-300 bg-white text-gray-700 hover:bg-gray-50 dark:border-grayDark-600/50 dark:border-b-grayDark-500/50 dark:bg-white/[0.04] dark:text-grayDark-200 dark:hover:bg-white/[0.07]";
const CARD_SEL =
    "border-blue-300 border-b-blue-400 bg-blue-50 text-gray-900 dark:border-blue-500/40 dark:border-b-blue-500/50 dark:bg-blue-500/10 dark:text-white";
const CARD_BAD =
    "border-red-300 border-b-red-400 bg-red-50 text-red-800 dark:border-red-500/40 dark:border-b-red-500/50 dark:bg-red-500/10 dark:text-red-200";

function shuffle(arr) {
    const a = [...arr];
    for (let i = a.length - 1; i > 0; i--) {
        const j = Math.floor(Math.random() * (i + 1));
        [a[i], a[j]] = [a[j], a[i]];
    }
    return a;
}

export default function RepsMatchMadness({
    trackSlug,
    unitKey,
    onClose,
    onComplete,
}) {
    const t = useTranslations();
    const locale = useLocale();
    // Nœud de chemin : la PREMIÈRE partie terminée complète le nœud (une fois).
    const completedRef = useRef(false);

    // phase : "intro" (règles + chargement) -> "play" -> "end".
    const [phase, setPhase] = useState("intro");
    const [pairs, setPairs] = useState(null); // null = chargement
    const [score, setScore] = useState(0);
    const [timeLeft, setTimeLeft] = useState(GAME_SECONDS);

    // Round courant : jusqu'à ROUND_SIZE paires, colonnes mélangées séparément.
    const [round, setRound] = useState([]); // [{id, l, r, found}]
    const [leftOrder, setLeftOrder] = useState([]);
    const [rightOrder, setRightOrder] = useState([]);
    const [selLeft, setSelLeft] = useState(null); // id sélectionné à gauche
    const [badPair, setBadPair] = useState(null); // {l, r} ids en erreur (flash)
    const poolRef = useRef([]); // paires restantes à distribuer
    const idRef = useRef(0);

    // Chargement des paires au montage (server-side assemble, on ne voit
    // passer que des {l, r}).
    useEffect(() => {
        let cancelled = false;
        fetch("/api/reps/madness", {
            method: "POST",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ trackSlug, unitKey, locale }),
        })
            .then((r) => (r.ok ? r.json() : { pairs: [] }))
            .then((data) => {
                if (!cancelled) setPairs(data.pairs || []);
            })
            .catch(() => {
                if (!cancelled) setPairs([]);
            });
        return () => {
            cancelled = true;
        };
    }, [trackSlug, unitKey, locale]);

    function dealRound() {
        const next = poolRef.current.splice(0, ROUND_SIZE).map((p) => ({
            ...p,
            id: idRef.current++,
            found: false,
        }));
        setRound(next);
        setLeftOrder(shuffle(next.map((p) => p.id)));
        setRightOrder(shuffle(next.map((p) => p.id)));
        setSelLeft(null);
    }

    function start() {
        poolRef.current = shuffle(pairs || []);
        idRef.current = 0;
        setScore(0);
        setTimeLeft(GAME_SECONDS);
        setPhase("play");
        dealRound();
    }

    // Chrono : décomptée seulement en phase de jeu.
    useEffect(() => {
        if (phase !== "play") return undefined;
        const id = setInterval(() => {
            setTimeLeft((s) => {
                if (s <= 1) {
                    clearInterval(id);
                    setPhase("end");
                    return 0;
                }
                return s - 1;
            });
        }, 1000);
        return () => clearInterval(id);
    }, [phase]);

    // Première partie terminée -> complétion du nœud (le parent décide quoi
    // créditer ; rejouer ne recomplète jamais).
    useEffect(() => {
        if (phase === "end" && onComplete && !completedRef.current) {
            completedRef.current = true;
            onComplete();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [phase]);

    const remaining = round.filter((p) => !p.found);

    function pickLeft(id) {
        if (phase !== "play") return;
        setSelLeft((prev) => (prev === id ? null : id));
    }

    function pickRight(id) {
        if (phase !== "play" || selLeft == null) return;
        if (selLeft === id) {
            // Bonne paire : elle s'envole, +1.
            setScore((s) => s + 1);
            setSelLeft(null);
            try {
                if (navigator.vibrate) navigator.vibrate(30);
            } catch (e) {
                /* ignore */
            }
            const next = round.map((p) =>
                p.id === id ? { ...p, found: true } : p
            );
            setRound(next);
            // Round vidé -> nouvelle donne (s'il reste des paires au pool).
            if (next.every((p) => p.found)) {
                if (poolRef.current.length) {
                    setTimeout(dealRound, 250);
                } else {
                    setTimeout(() => setPhase("end"), 350);
                }
            }
        } else {
            // Mauvaise paire : flash rouge sur les deux cartes.
            setBadPair({ l: selLeft, r: id });
            setSelLeft(null);
            try {
                if (navigator.vibrate) navigator.vibrate(80);
            } catch (e) {
                /* ignore */
            }
            setTimeout(() => setBadPair(null), 350);
        }
    }

    const byId = useMemo(() => {
        const m = {};
        round.forEach((p) => {
            m[p.id] = p;
        });
        return m;
    }, [round]);

    const notEnough = Array.isArray(pairs) && pairs.length < ROUND_SIZE;

    return (
        <div className="fixed inset-0 z-[100] flex flex-col bg-white dark:bg-[#0E161D]">
            {/* Barre du haut : chrono + score + fermer. */}
            <div className="mx-auto flex w-full max-w-xl items-center gap-4 px-4 pb-2 pt-4">
                <button
                    type="button"
                    aria-label={t("reps_madness_close")}
                    onClick={onClose}
                    className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full text-gray-400 transition-colors hover:bg-gray-100 hover:text-gray-600 dark:hover:bg-white/10 dark:hover:text-grayDark-200"
                >
                    <SolarIcon name="close-circle-linear" className="text-2xl" />
                </button>
                <div className="h-2.5 flex-1 overflow-hidden rounded-full bg-gray-100 dark:bg-white/10">
                    <div
                        className="h-full rounded-full bg-orange-500 transition-[width] duration-1000 ease-linear"
                        style={{
                            width: `${(timeLeft / GAME_SECONDS) * 100}%`,
                        }}
                    />
                </div>
                <span className="w-10 shrink-0 text-right font-geistMono text-sm font-bold text-gray-600 dark:text-grayDark-300">
                    {timeLeft}s
                </span>
                <span className="flex shrink-0 items-center gap-1.5 rounded-full bg-orange-500/10 px-3 py-1 text-sm font-extrabold text-orange-600 dark:text-orange-400">
                    <SolarIcon name="bolt-bold" className="text-base" />
                    {score}
                </span>
            </div>

            <div className="mx-auto flex w-full max-w-xl flex-1 flex-col justify-center px-4 pb-8">
                {phase === "intro" && (
                    <div className="text-center">
                        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-500/10">
                            <SolarIcon
                                name="bolt-bold"
                                className="text-4xl text-orange-500"
                            />
                        </div>
                        <h2 className="text-2xl font-extrabold text-gray-900 dark:text-white">
                            {t("reps_madness_title")}
                        </h2>
                        <p className="mx-auto mt-2 max-w-sm text-sm leading-relaxed text-gray-500 dark:text-grayDark-400">
                            {t("reps_madness_subtitle", {
                                seconds: GAME_SECONDS,
                            })}
                        </p>
                        {notEnough ? (
                            <p className="mt-6 text-sm text-gray-400">
                                {t("reps_madness_empty")}
                            </p>
                        ) : (
                            <button
                                type="button"
                                disabled={pairs === null}
                                onClick={start}
                                className="mt-7 rounded-2xl border-b-4 border-orange-700 bg-orange-500 px-10 py-3.5 text-base font-extrabold uppercase text-white transition-transform active:translate-y-1 active:border-b-0 disabled:opacity-50"
                            >
                                {pairs === null ? "…" : t("reps_madness_go")}
                            </button>
                        )}
                    </div>
                )}

                {phase === "play" && (
                    <div className="grid grid-cols-2 gap-3 sm:gap-5">
                        <div className="space-y-2.5">
                            <AnimatePresence>
                                {leftOrder
                                    .filter((id) => byId[id] && !byId[id].found)
                                    .map((id) => (
                                        <motion.button
                                            key={`l${id}`}
                                            layout
                                            type="button"
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, scale: 0.92 }}
                                            transition={{ duration: 0.18 }}
                                            onClick={() => pickLeft(id)}
                                            className={`${CARD_BASE} ${
                                                badPair && badPair.l === id
                                                    ? CARD_BAD
                                                    : selLeft === id
                                                      ? CARD_SEL
                                                      : CARD_NEUTRAL
                                            }`}
                                        >
                                            {byId[id].l}
                                        </motion.button>
                                    ))}
                            </AnimatePresence>
                        </div>
                        <div className="space-y-2.5">
                            <AnimatePresence>
                                {rightOrder
                                    .filter((id) => byId[id] && !byId[id].found)
                                    .map((id) => (
                                        <motion.button
                                            key={`r${id}`}
                                            layout
                                            type="button"
                                            initial={{ opacity: 0, y: 8 }}
                                            animate={{ opacity: 1, y: 0 }}
                                            exit={{ opacity: 0, scale: 0.92 }}
                                            transition={{ duration: 0.18 }}
                                            onClick={() => pickRight(id)}
                                            className={`${CARD_BASE} ${
                                                badPair && badPair.r === id
                                                    ? CARD_BAD
                                                    : CARD_NEUTRAL
                                            }`}
                                        >
                                            {byId[id].r}
                                        </motion.button>
                                    ))}
                            </AnimatePresence>
                        </div>
                        {remaining.length === 0 && (
                            <span className="sr-only">…</span>
                        )}
                    </div>
                )}

                {phase === "end" && (
                    <div className="text-center">
                        <div className="mx-auto mb-5 flex h-16 w-16 items-center justify-center rounded-2xl bg-orange-500/10">
                            <SolarIcon
                                name="bolt-bold"
                                className="text-4xl text-orange-500"
                            />
                        </div>
                        <p className="text-5xl font-extrabold text-gray-900 dark:text-white">
                            {score}
                        </p>
                        <p className="mt-2 text-sm text-gray-500 dark:text-grayDark-400">
                            {t("reps_madness_score", { count: score })}
                        </p>
                        <div className="mt-7 flex items-center justify-center gap-3">
                            <button
                                type="button"
                                onClick={start}
                                className="rounded-2xl border-b-4 border-orange-700 bg-orange-500 px-8 py-3 text-sm font-extrabold uppercase text-white transition-transform active:translate-y-1 active:border-b-0"
                            >
                                {t("reps_madness_replay")}
                            </button>
                            <button
                                type="button"
                                onClick={onClose}
                                className="rounded-2xl border-2 border-b-4 border-gray-200 border-b-gray-300 bg-white px-8 py-3 text-sm font-extrabold uppercase text-gray-600 transition-transform active:translate-y-1 active:border-b-2 dark:border-grayDark-600/50 dark:border-b-grayDark-500/50 dark:bg-white/[0.04] dark:text-grayDark-300"
                            >
                                {t("reps_madness_close")}
                            </button>
                        </div>
                    </div>
                )}
            </div>
        </div>
    );
}
